import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShoppingCart, MessageCircle, Wallet, Truck, PartyPopper, ArrowRight, ArrowLeft, X } from "lucide-react";

const STEPS = [
  { icon: ShoppingCart, title: "1. Arma tu carrito", text: "Explora el catálogo y agrega productos. Si un producto tiene variantes (tallas o presentaciones), elige la tuya antes de agregarlo. No necesitas registrarte." },
  { icon: MessageCircle, title: "2. Envía tu pedido por WhatsApp", text: "Al finalizar, tu carrito se convierte en un mensaje. Solo agrega tu nombre completo, dirección (link de Maps o Waze) y teléfono." },
  { icon: Wallet, title: "3. Paga al recibir (GAM)", text: "Dentro del Gran Área Metropolitana pagas al recibir tu pedido: en efectivo, por SINPE Móvil o transferencia. ¡Como más te acomode!" },
  { icon: Truck, title: "4. Envíos", text: "Coordinamos la entrega por WhatsApp. Los envíos fuera del GAM tienen un costo de ₡2.000." },
  { icon: PartyPopper, title: "5. ¡Listo!", text: "Confirmamos disponibilidad y total, y tu mascota disfruta muy pronto de sus productos. 🐾" },
];

const KEY = "pm_onboarded_v1";

export const Onboarding = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!localStorage.getItem(KEY)) setOpen(true);
    }, 1200);
    return () => clearTimeout(t);
  }, []);

  const close = () => { localStorage.setItem(KEY, "1"); setOpen(false); };
  const s = STEPS[step];
  const last = step === STEPS.length - 1;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="onboarding-modal" className="max-w-md rounded-3xl bg-cream border-[#E3E7E2] p-0 overflow-hidden">
        <DialogTitle className="sr-only">Cómo comprar</DialogTitle>
        <DialogDescription className="sr-only">Guía paso a paso para comprar en The Pet Shopping Co</DialogDescription>

        <button onClick={close} data-testid="onboarding-skip" className="absolute top-3 right-3 z-10 grid place-items-center w-8 h-8 rounded-full bg-white/80 text-charcoal hover:bg-coral hover:text-white transition-colors"><X className="w-4 h-4" /></button>

        <div className="bg-gradient-to-br from-peach to-sage px-6 pt-10 pb-8 text-center">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }} transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }} className="grid place-items-center w-20 h-20 mx-auto rounded-3xl bg-white text-coral card-shadow">
              <s.icon className="w-10 h-10" />
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="px-6 pb-6 pt-5 text-center">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }}>
              <h2 className="font-display text-2xl font-bold text-charcoal">{s.title}</h2>
              <p className="text-charcoal/75 mt-2 leading-relaxed min-h-[4.5rem]">{s.text}</p>
            </motion.div>
          </AnimatePresence>

          <div className="flex items-center justify-center gap-1.5 my-5">
            {STEPS.map((_, i) => (
              <button key={i} onClick={() => setStep(i)} className={`h-2 rounded-full transition-all ${i === step ? "w-6 bg-coral" : "w-2 bg-[#E3E7E2]"}`} aria-label={`Paso ${i + 1}`} />
            ))}
          </div>

          <div className="flex items-center gap-3">
            {step > 0 && (
              <button onClick={() => setStep((v) => v - 1)} className="grid place-items-center w-12 h-12 rounded-2xl bg-white border border-[#E3E7E2] text-charcoal hover:bg-sage/40"><ArrowLeft className="w-5 h-5" /></button>
            )}
            <button
              data-testid="onboarding-next"
              onClick={() => (last ? close() : setStep((v) => v + 1))}
              className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-coral text-white font-bold hover:bg-coral-dark active:scale-95 transition-all"
            >
              {last ? "¡Empezar a comprar!" : "Siguiente"} {!last && <ArrowRight className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
